import React from 'react';
import { Briefcase } from 'lucide-react';
import { useWorkCounts } from './useWorkCounts';

export type WorkTabKey = 'inbox' | 'audit' | 'compliance' | 'statutory' | 'control-testing' | 'it-assessment' | 'risk-assessments' | 'process-walkthroughs-design' | 'process-walkthroughs-compliance' | 'renewals' | 'reviews' | 'capa' | 'tasks' | 'projects' | 'meetings' | 'litigation' | 'leave-approvals' | 'performance-measures';

// 'active'  = Action Centre — my pending/actionable items, fully interactive.
// 'archive' = History — my closed/completed/past records, strictly read-only.
// The two surfaces share every component; only the query and read-only gates differ.
export type WorkScope = 'active' | 'archive';

export interface WorkTabMeta {
  key: WorkTabKey;
  label: string;
}

export const workTabs: WorkTabMeta[] = [
  { key: 'tasks', label: 'My Tasks' },
  { key: 'projects', label: 'My Projects' },
  { key: 'compliance', label: 'Compliance (P)' },
  { key: 'statutory', label: 'Compliance (S)' },
  { key: 'litigation', label: 'Litigation' },
  { key: 'audit', label: 'Audit' },
  { key: 'control-testing', label: 'CAT' },
  { key: 'it-assessment', label: 'ITAT' },
  { key: 'risk-assessments', label: 'RAT' },
  { key: 'process-walkthroughs-design', label: 'PWT (D)' },
  { key: 'process-walkthroughs-compliance', label: 'PWT (C)' },
  { key: 'renewals', label: 'Renewals' },
  { key: 'reviews', label: 'Reviews' },
  { key: 'capa', label: 'CAPA' },
  { key: 'meetings', label: 'Meetings' },
  { key: 'leave-approvals', label: 'Leave Approvals' },
];

interface WorkTabsProps {
  activeTab: WorkTabKey;
  onTabChange: (key: WorkTabKey) => void;
  scope?: WorkScope;
}

/**
 * Action Centre side-rail. Tabs are stacked vertically in a slim left rail
 * (~15% width — see .work-shell / .work-sidenav in global.css) so the growing
 * tab count no longer overflows a horizontal header. Each tab shows its pending
 * count (red badge); the rail header shows the total across all tabs. All
 * counts load in one parallel burst via useWorkCounts.
 */
const WorkTabs: React.FC<WorkTabsProps> = ({ activeTab, onTabChange, scope = 'active' }) => {
  const { counts, total, loading } = useWorkCounts(scope);
  const title = scope === 'archive' ? 'History' : 'Action Centre';
  // Rail header: grand total of all my open items, segregated from the per-tab
  // breakdown by a thin line (.work-sidenav-label border-bottom). The tabs follow.
  const totalLabel = scope === 'archive' ? 'Completed' : 'Open Items';
  return (
    <nav className={`work-sidenav${scope === 'archive' ? ' archive' : ''}`} role="tablist" aria-label={title}>
      <div className="work-sidenav-label">
        <span className="work-sidenav-icon"><Briefcase size={16} strokeWidth={1.75} /></span>
        <span className="work-sidenav-title">{totalLabel}</span>
        {!loading && (
          <span
            className={`work-sidenav-total${total === 0 ? ' is-zero' : ''}`}
            data-testid="CNT_work_total"
          >
            {total}
          </span>
        )}
      </div>
      {workTabs.map(t => {
        const n = counts[t.key] ?? 0;
        return (
          <button
            key={t.key}
            role="tab"
            aria-selected={activeTab === t.key}
            className={`work-sidenav-tab${activeTab === t.key ? ' active' : ''}`}
            onClick={() => onTabChange(t.key)}
            data-testid={`TAB_work_sidenav_${t.key}`}
          >
            <span className="work-sidenav-tab-label">{t.label}</span>
            {!loading && (
              <span
                className={`work-sidenav-count${n === 0 ? ' is-zero' : ''}`}
                data-testid={`CNT_work_${t.key}`}
              >
                {n}
              </span>
            )}
          </button>
        );
      })}
    </nav>
  );
};

export default WorkTabs;
